'use client'

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { ChevronDown, ChevronRight, Filter, Loader2, PackageOpen, SlidersHorizontal, Star, X } from 'lucide-react'
import { formatPrice, getDisplayDiscounts, getEffectivePrice, getProductTags, hasLoyaltyTierBenefits, isLoyaltyActive } from '@/utils/util'
import { SORT_OPTIONS, SPECIAL_CATEGORY_LABELS, SPECIAL_CATEGORY_FILTERS, findCategoryById, getCategoryDescendantIds, buildInitialProductFilters, type SpecialCategoryFilter } from '@/utils/product'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { useInfiniteProducts } from '@/hooks/use-infinite-products'
import { useCart } from '@/lib/commerce/hooks'
import { Session } from '@/utils/types'
import ProductCard from './ProductCard'

const PRICE_MIN = 0
const PRICE_MAX = 2000000

const RATING_BUCKETS = [
  { id: '4.5', label: '4.5+', min: 4.5 },
  { id: '4.0', label: '4.0+', min: 4.0 },
  { id: '3.5', label: '3.5+', min: 3.5 },
]

function getProductPrice(p: any, isLoggedIn: boolean) {
  const variant = p?.variants?.[0] || p?.variant
  if (!variant) return 0
  const discounts = getDisplayDiscounts(p, isLoggedIn, variant?.id)
  const eff = getEffectivePrice(variant, discounts, isLoggedIn)
  return eff ?? variant?.price ?? 0
}

function makeBins(prices: number[], min: number, max: number, n = 24) {
  const w = (max - min) / n || 1
  const bins = Array.from({ length: n }, () => 0)
  prices.forEach(p => {
    const i = Math.min(n - 1, Math.max(0, Math.floor((p - min) / w)))
    bins[i]++
  })
  return bins
}

function PriceFilter({ lo, hi, bins, onChange }: { lo: number, hi: number, bins: number[], onChange: (lo: number, hi: number) => void }) {
  const [loText, setLoText] = useState(String(lo))
  const [hiText, setHiText] = useState(String(hi))
  useEffect(() => { setLoText(String(lo)) }, [lo])
  useEffect(() => { setHiText(String(hi)) }, [hi])

  const commit = (which: 'lo' | 'hi', raw: string) => {
    const n = Math.max(0, Number(raw.replace(/[^\d]/g, '')) || 0)
    if (which === 'lo') onChange(Math.max(PRICE_MIN, Math.min(n, hi - 1000)), hi)
    else onChange(lo, Math.min(PRICE_MAX, Math.max(n, lo + 1000)))
  }

  const maxBin = Math.max(1, ...bins)
  const w = (PRICE_MAX - PRICE_MIN) / bins.length || 1

  return (
    <div className="space-y-3">
      <div className="flex items-stretch gap-2">
        <label className="flex-1 min-w-0 rounded-lg px-3 py-2 cursor-text transition-colors focus-within:border-white/25" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="text-[9px] uppercase tracking-[0.16em]" style={{ color: '#a8a8b3' }}>Эхлэх</div>
          <input
            value={loText}
            onChange={e => setLoText(e.target.value)}
            onBlur={() => commit('lo', loText)}
            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
            className="w-full bg-transparent text-[13px] tabular-nums text-white border-0 outline-none focus:outline-none focus:ring-0 p-0"
            inputMode="numeric"
            placeholder="0"
          />
        </label>
        <label className="flex-1 min-w-0 rounded-lg px-3 py-2 cursor-text transition-colors focus-within:border-white/25" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="text-[9px] uppercase tracking-[0.16em]" style={{ color: '#a8a8b3' }}>Дуусах</div>
          <input
            value={hiText}
            onChange={e => setHiText(e.target.value)}
            onBlur={() => commit('hi', hiText)}
            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
            className="w-full bg-transparent text-[13px] tabular-nums text-white border-0 outline-none focus:outline-none focus:ring-0 p-0"
            inputMode="numeric"
            placeholder={String(PRICE_MAX)}
          />
        </label>
      </div>
      <div className="flex items-end gap-0.5 h-10">
        {bins.map((c, i) => {
          const x = PRICE_MIN + (i + 0.5) * w
          const inRange = x >= lo && x <= hi
          const h = Math.max(6, Math.round((c / maxBin) * 100))
          return (
            <div
              key={i}
              className="flex-1 rounded-sm transition-colors"
              style={{ height: `${h}%`, background: inRange ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.12)' }}
            />
          )
        })}
      </div>
      <div className="flex items-center justify-between text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
        <span className="tabular-nums">{formatPrice(lo)}</span>
        <span className="tabular-nums">{formatPrice(hi)}</span>
      </div>
    </div>
  )
}

function RatingFilter({ value, onChange }: { value: number | null, onChange: (v: number | null) => void }) {
  return (
    <div className="space-y-1">
      {RATING_BUCKETS.map(b => {
        const active = value === b.min
        const filled = Math.floor(b.min)
        return (
          <label key={b.id} className="flex items-center gap-3 py-1.5 cursor-pointer">
            <input
              type="radio"
              checked={active}
              onChange={() => onChange(active ? null : b.min)}
              className="sr-only"
            />
            <span
              className="w-4 h-4 rounded-full inline-flex items-center justify-center shrink-0"
              style={{
                border: `1.5px solid ${active ? 'white' : 'rgba(255,255,255,0.3)'}`,
                background: active ? 'white' : 'transparent',
              }}
            >
              {active && <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#0a0a0c' }} />}
            </span>
            <span className="inline-flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map(i => (
                <Star
                  key={i}
                  className="h-3 w-3"
                  fill={i <= filled ? 'currentColor' : 'none'}
                  style={{ color: i <= filled ? '#fcd34d' : 'rgba(255,255,255,0.3)' }}
                  strokeWidth={1.5}
                />
              ))}
            </span>
            <span className="text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>дээш</span>
          </label>
        )
      })}
    </div>
  )
}

function FilterGroup({ title, count, defaultOpen = true, children }: { title: string, count?: number | string, defaultOpen?: boolean, children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="py-5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between">
        <div className="flex items-baseline gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white">{title}</span>
          {count != null && <span className="text-[11px] tabular-nums" style={{ color: 'rgba(255,255,255,0.4)' }}>{count}</span>}
        </div>
        <ChevronDown
          className="h-3.5 w-3.5 transition-transform duration-200"
          style={{ color: 'rgba(255,255,255,0.6)', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </button>
      {open && <div className="mt-4">{children}</div>}
    </div>
  )
}

function FilterCheck({ label, count, active, onChange }: { label: string, count?: number | string, active?: boolean, onChange?: () => void }) {
  return (
    <label className="flex items-center gap-3 py-1.5 cursor-pointer text-[13px]">
      <input type="checkbox" checked={!!active} onChange={() => onChange?.()} className="sr-only" />
      <span
        className="w-4 h-4 rounded inline-flex items-center justify-center shrink-0"
        style={{
          background: active ? 'white' : 'transparent',
          border: `1px solid ${active ? 'white' : 'rgba(255,255,255,0.14)'}`,
        }}
      >
        {active && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0a0a0c" strokeWidth="3.5">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        )}
      </span>
      <span className="flex-1 truncate" style={{ color: active ? 'white' : 'rgba(255,255,255,0.78)' }}>{label}</span>
      {count != null && <span className="text-[11px] tabular-nums shrink-0" style={{ color: 'rgba(255,255,255,0.4)' }}>{count}</span>}
    </label>
  )
}

function CategoryTree({ categories, activeId, onSelect }: { categories: any[], activeId: string | null, onSelect: (id: string | null) => void }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const toggle = (id: string) => setExpanded(e => ({ ...e, [id]: !e[id] }))

  const renderNode = (cat: any, depth: number = 0): React.ReactElement => {
    const id = cat.slug || cat.id
    const hasChildren = cat.children && cat.children.length > 0
    const isOpen = !!expanded[id]
    const isActive = activeId === id
    return (
      <Fragment key={cat.id}>
        <div className="flex items-center gap-1" style={{ paddingLeft: depth * 12 }}>
          {hasChildren ? (
            <button
              onClick={() => toggle(id)}
              aria-label={isOpen ? 'Хаах' : 'Дэлгэх'}
              className="w-4 h-4 inline-flex items-center justify-center shrink-0 rounded transition-colors hover:bg-white/5"
              style={{ color: 'rgba(255,255,255,0.5)' }}
            >
              <ChevronRight className="h-3 w-3 transition-transform" style={{ transform: isOpen ? 'rotate(90deg)' : 'none' }} />
            </button>
          ) : (
            <span className="w-4 h-4 shrink-0" />
          )}
          <button
            onClick={() => onSelect(id)}
            className="flex-1 flex items-center justify-between py-1.5 text-left text-[13px] transition-colors truncate"
            style={{ color: isActive ? 'white' : 'rgba(255,255,255,0.78)' }}
          >
            <span className="truncate">{cat.name}</span>
          </button>
        </div>
        {hasChildren && isOpen && (
          <div style={{ borderLeft: '1px solid rgba(255,255,255,0.07)', marginLeft: depth * 12 + 8 }}>
            {cat.children.map((c: any) => renderNode(c, depth + 1))}
          </div>
        )}
      </Fragment>
    )
  }

  return (
    <div className="space-y-0.5">
      <button
        onClick={() => onSelect(null)}
        className="w-full flex items-center py-1.5 text-left text-[13px] transition-colors hover:text-white"
        style={{ color: activeId === null ? 'white' : 'rgba(255,255,255,0.78)' }}
      >
        Бүгд
      </button>
      {categories.map((c: any) => renderNode(c))}
    </div>
  )
}

function FilterSidebar({
  categories, brands, tags,
  activeCategoryId, setCategoryParams,
  activeBrandIds, toggleBrand,
  activeTags, toggleTag,
  activeSpecial, setSpecial,
  priceLo, priceHi, onPriceChange, priceBins,
  ratingMin, setRatingMin,
  showLoyalty,
}: {
  categories: any[], brands: any[], tags: string[],
  activeCategoryId: string | null, setCategoryParams: (id: string | null) => void,
  activeBrandIds: string[], toggleBrand: (id: string) => void,
  activeTags: string[], toggleTag: (t: string) => void,
  activeSpecial: SpecialCategoryFilter | null, setSpecial: (s: SpecialCategoryFilter | null) => void,
  priceLo: number, priceHi: number, onPriceChange: (lo: number, hi: number) => void, priceBins: number[],
  ratingMin: number | null, setRatingMin: (v: number | null) => void,
  showLoyalty?: boolean,
}) {
  const specials: SpecialCategoryFilter[] = SPECIAL_CATEGORY_FILTERS.filter((id) => id !== 'loyalty' || showLoyalty)
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mt-2 sm:mt-0 mb-1" style={{ color: '#a8a8b3' }}>Шүүлтүүр</div>

      <FilterGroup title="Бүлэг" defaultOpen={true}>
        <div className="space-y-1">
          {specials.map(s => (
            <FilterCheck
              key={s}
              label={SPECIAL_CATEGORY_LABELS[s]}
              active={activeSpecial === s}
              onChange={() => setSpecial(activeSpecial === s ? null : s)}
            />
          ))}
        </div>
      </FilterGroup>

      {categories.length > 0 && (
        <FilterGroup title="Ангилал" defaultOpen={true}>
          <CategoryTree categories={categories} activeId={activeCategoryId} onSelect={setCategoryParams} />
        </FilterGroup>
      )}

      <FilterGroup title="Үнэ" defaultOpen={true}>
        <PriceFilter lo={priceLo} hi={priceHi} bins={priceBins} onChange={onPriceChange} />
      </FilterGroup>

      <FilterGroup title="Үнэлгээ" defaultOpen={true}>
        <RatingFilter value={ratingMin} onChange={setRatingMin} />
      </FilterGroup>

      {brands.length > 0 && (
        <FilterGroup title="Брэнд" count={brands.length}>
          <div className="space-y-1">
            {brands.slice(0, 10).map((b: any) => (
              <FilterCheck
                key={b.id}
                label={b.name}
                active={activeBrandIds.includes(b.id)}
                onChange={() => toggleBrand(b.id)}
              />
            ))}
            {brands.length > 10 && (
              <button className="mt-2 text-[11.5px] font-medium text-white hover:opacity-80">+ {brands.length - 10} бусад брэнд</button>
            )}
          </div>
        </FilterGroup>
      )}

      {tags.length > 0 && (
        <FilterGroup title="Шошго" count={tags.length}>
          <div className="space-y-1">
            {tags.slice(0, 12).map(t => (
              <FilterCheck
                key={t}
                label={t}
                active={activeTags.includes(t)}
                onChange={() => toggleTag(t)}
              />
            ))}
          </div>
        </FilterGroup>
      )}
    </div>
  )
}

export default function ProductList({ vendor, categoryList, brandList, session }: { vendor: any, categoryList: any, brandList: any, wishlist?: any, session?: Session | null }) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const { loyaltySummary } = useCart()
  const showLoyaltyFilter = isLoyaltyActive(vendor) && hasLoyaltyTierBenefits(loyaltySummary)

  const categories = categoryList || []
  const brands = brandList || []
  const isLoggedIn = !!session?.user?.id
  const [tags, setTags] = useState<string[]>([])
  const [sort, setSort] = useState('latest')
  const [sortOpen, setSortOpen] = useState(false)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [lo, setLo] = useState<number>(Number(searchParams?.get('priceMin')) || PRICE_MIN)
  const [hi, setHi] = useState<number>(Number(searchParams?.get('priceMax')) || PRICE_MAX)
  const [ratingMin, setRatingMin] = useState<number | null>(() => {
    const v = Number(searchParams?.get('rating'))
    return v && !Number.isNaN(v) ? v : null
  })

  const getInitialFilters = useCallback(() => buildInitialProductFilters(searchParams, categoryList), [searchParams, categoryList])

  const { products, isLoading, isLoadingMore, metadata, loadMore, updateFilters, hasMore, pendingCount } = useInfiniteProducts({
    vendorId: vendor?.id,
    limit: 20,
    initialFilters: getInitialFilters(),
    session,
  })

  useInfiniteScroll({
    targetRef: loadMoreRef,
    onLoadMore: loadMore,
    enabled: hasMore && !isLoading && !isLoadingMore,
  })

  useEffect(() => {
    if (products && products.length > 0) {
      const allTags = products.flatMap(p => getProductTags(p))
      const uniqueTags = [...new Set(allTags)].filter(t => t !== '').sort((a, b) => a.localeCompare(b))
      setTags(uniqueTags)
    }
  }, [products])

  const activeBrandIds = useMemo(() => {
    const v = searchParams.get('brand')
    return v ? v.split(',') : []
  }, [searchParams])
  const activeTags = useMemo(() => {
    const v = searchParams.get('tag')
    return v ? v.split(',') : []
  }, [searchParams])
  const activeCategoryId = searchParams.get('category')
  const activeSpecial = (() => {
    const v = searchParams.get('filter') as SpecialCategoryFilter | null
    return v && SPECIAL_CATEGORY_FILTERS.includes(v) ? v : null
  })()

  const currentCategory = useMemo(() => {
    if (activeCategoryId && SPECIAL_CATEGORY_FILTERS.includes(activeCategoryId as SpecialCategoryFilter)) {
      return { name: SPECIAL_CATEGORY_LABELS[activeCategoryId as SpecialCategoryFilter], id: activeCategoryId }
    }
    if (activeCategoryId) {
      return findCategoryById(categoryList, activeCategoryId)
    }
    if (activeSpecial) {
      return { name: SPECIAL_CATEGORY_LABELS[activeSpecial], id: activeSpecial }
    }
    if (activeBrandIds.length > 0 && brands.length > 0) {
      const selected = brands.filter((b: any) => activeBrandIds.includes(b.id))
      if (selected.length === 1) return { name: selected[0].name, id: 'filtered', thumbnail: selected[0].thumbnail }
      if (selected.length > 1) return { name: `${selected.length} брэнд`, id: 'filtered' }
    }
    return null
  }, [activeCategoryId, activeSpecial, activeBrandIds, brands, categoryList])

  const sortHandler = (value: string) => {
    setSort(value)
    setSortOpen(false)
    updateFilters({ sort: value })
  }

  const pushParams = (next: URLSearchParams) => {
    router.push(`${pathname}?${next.toString()}`, { scroll: false })
  }

  const setCategoryParams = (id: string | null) => {
    const next = new URLSearchParams(searchParams.toString())
    next.delete('filter')
    if (id == null) {
      next.delete('category')
      updateFilters({ category: null, filter: null })
    } else if (SPECIAL_CATEGORY_FILTERS.includes(id as SpecialCategoryFilter)) {
      next.delete('category')
      next.set('filter', id)
      updateFilters({ category: null, filter: id })
    } else {
      const found = findCategoryById(categoryList, id)
      const ids = found ? getCategoryDescendantIds(found) : [id]
      next.set('category', id)
      updateFilters({ category: ids.join(','), filter: null })
    }
    pushParams(next)
  }

  const setSpecial = (s: SpecialCategoryFilter | null) => {
    const next = new URLSearchParams(searchParams.toString())
    next.delete('category')
    if (s == null) {
      next.delete('filter')
      updateFilters({ category: null, filter: null })
    } else {
      next.set('filter', s)
      updateFilters({ category: null, filter: s })
    }
    pushParams(next)
  }

  const onPriceChange = (newLo: number, newHi: number) => {
    setLo(newLo)
    setHi(newHi)
    const next = new URLSearchParams(searchParams.toString())
    if (newLo > PRICE_MIN) next.set('priceMin', String(newLo)); else next.delete('priceMin')
    if (newHi < PRICE_MAX) next.set('priceMax', String(newHi)); else next.delete('priceMax')
    pushParams(next)
  }

  const setRatingMinParam = (v: number | null) => {
    setRatingMin(v)
    const next = new URLSearchParams(searchParams.toString())
    if (v != null) next.set('rating', String(v)); else next.delete('rating')
    pushParams(next)
  }

  const toggleListParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams.toString())
    const current = (next.get(key) || '').split(',').filter(Boolean)
    const has = current.includes(value)
    const updated = has ? current.filter(v => v !== value) : [...current, value]
    if (updated.length > 0) next.set(key, updated.join(','))
    else next.delete(key)
    updateFilters({ [key]: updated.length > 0 ? updated.join(',') : null })
    pushParams(next)
  }

  const clearAll = () => {
    setLo(PRICE_MIN)
    setHi(PRICE_MAX)
    setRatingMin(null)
    updateFilters({ brand: null, tag: null, category: null, filter: null })
    router.push(pathname, { scroll: false })
  }

  const prices = useMemo(() => products.map((p: any) => getProductPrice(p, isLoggedIn)), [products, isLoggedIn])
  const priceBins = useMemo(() => makeBins(prices, PRICE_MIN, PRICE_MAX, 24), [prices])
  const displayedProducts = useMemo(() => {
    let xs = products
    if (lo > PRICE_MIN || hi < PRICE_MAX) {
      xs = xs.filter((p: any) => {
        const price = getProductPrice(p, isLoggedIn)
        return price >= lo && price <= hi
      })
    }
    if (ratingMin != null) xs = xs.filter((p: any) => (p.averageRating ?? 0) >= ratingMin)
    return xs
  }, [products, isLoggedIn, lo, hi, ratingMin])

  type ChipType = 'brand' | 'tag' | 'category' | 'special' | 'price' | 'rating'
  const activeChips: { type: ChipType, value: string, label: string }[] = []
  brands.filter((b: any) => activeBrandIds.includes(b.id)).forEach((b: any) => activeChips.push({ type: 'brand', value: b.id, label: b.name }))
  activeTags.forEach(t => activeChips.push({ type: 'tag', value: t, label: t }))
  if (currentCategory && currentCategory.id !== 'filtered') {
    activeChips.push({ type: 'category', value: String(currentCategory.id), label: currentCategory.name })
  }
  if (lo > PRICE_MIN || hi < PRICE_MAX) {
    activeChips.push({ type: 'price', value: 'price', label: `${formatPrice(lo)} — ${formatPrice(hi)}` })
  }
  if (ratingMin != null) {
    activeChips.push({ type: 'rating', value: String(ratingMin), label: `Үнэлгээ ${ratingMin}+` })
  }

  const removeChip = (chip: typeof activeChips[number]) => {
    if (chip.type === 'brand') toggleListParam('brand', chip.value)
    else if (chip.type === 'tag') toggleListParam('tag', chip.value)
    else if (chip.type === 'category') setCategoryParams(null)
    else if (chip.type === 'special') setSpecial(null)
    else if (chip.type === 'price') onPriceChange(PRICE_MIN, PRICE_MAX)
    else if (chip.type === 'rating') setRatingMinParam(null)
  }

  const currentSortLabel = (SORT_OPTIONS.find(o => o.value === sort)?.label) || 'Сонголт'
  const heading = currentCategory?.name || 'Бараа бүтээгдэхүүн'

  return (
    <div>
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-4 sm:pt-10 pb-8">
        <div className="hidden md:flex items-center gap-1.5 text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
          <a href="/" className="hover:text-white transition-colors">Нүүр</a>
          <span style={{ opacity: 0.4 }}>/</span>
          <span className="text-white">{heading}</span>
        </div>
        <div className="mt-4 flex items-end justify-between gap-4 sm:gap-8 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>
              {activeSpecial ? 'Бүлэг' : 'Ангилал'}
            </div>
            <h1
              className="mt-2 font-semibold tracking-[-0.025em] leading-[0.95] text-white"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(28px, 6vw, 56px)' }}
            >
              {heading}
            </h1>
            <div className="mt-2 sm:hidden text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
              <span className="tabular-nums text-white font-medium">{isLoading ? '—' : metadata.total}</span> бүтээгдэхүүн
            </div>
          </div>
          <div className="hidden sm:block text-right shrink-0">
            <div
              className="font-semibold tabular-nums text-white leading-none"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 30 }}
            >
              {isLoading ? '—' : metadata.total}
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.55)' }}>бүтээгдэхүүн</div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-6">
        <div
          className="flex items-center justify-between gap-4 py-4 flex-wrap"
          style={{ borderTop: '1px solid rgba(255,255,255,0.07)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {activeChips.length > 0 ? (
              <>
                <span className="text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Идэвхтэй:</span>
                {activeChips.map(chip => (
                  <button
                    key={`${chip.type}-${chip.value}`}
                    onClick={() => removeChip(chip)}
                    className="h-7 pl-3 pr-1.5 inline-flex items-center gap-1.5 rounded-full text-[11.5px]"
                    style={{ background: 'white', color: '#0a0a0c' }}
                  >
                    <span className="truncate max-w-[160px]">{chip.label}</span>
                    <span className="w-4 h-4 rounded-full inline-flex items-center justify-center shrink-0" style={{ background: 'rgba(0,0,0,0.15)' }}>
                      <X className="h-2.5 w-2.5" strokeWidth={3} />
                    </span>
                  </button>
                ))}
                <button onClick={clearAll} className="h-7 px-3 text-[11.5px] font-medium hover:text-white transition-colors" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  Бүгдийг арилгах
                </button>
              </>
            ) : (
              <span className="text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {isLoading ? 'Ачаалж байна…' : `${displayedProducts.length} / ${metadata.total} үр дүн`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setMobileFiltersOpen(true)}
              className="lg:hidden h-9 px-4 inline-flex items-center gap-2 rounded-full text-[12.5px] font-medium transition-colors hover:bg-white/10"
              style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
            >
              <Filter className="h-3.5 w-3.5" /> Шүүлт
            </button>
            <div className="relative">
              <button
                onClick={() => setSortOpen(o => !o)}
                aria-label="Эрэмбэлэх"
                className="h-9 px-3 sm:px-4 rounded-full text-[12.5px] font-medium inline-flex items-center gap-2 transition-colors hover:bg-white/10"
                style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Эрэмбэлэх</span>
                <span className="hidden sm:inline" style={{ color: 'rgba(255,255,255,0.6)' }}>· {currentSortLabel}</span>
                <ChevronDown className="h-3 w-3 transition-transform" style={{ transform: sortOpen ? 'rotate(180deg)' : 'none' }} />
              </button>
              {sortOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setSortOpen(false)} />
                  <div
                    className="absolute top-full right-0 mt-2 rounded-xl py-1 min-w-[180px] z-40"
                    style={{ background: 'rgba(8,8,10,0.96)', border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 16px 40px rgba(0,0,0,0.5)' }}
                  >
                    {SORT_OPTIONS.map(o => (
                      <button
                        key={o.value}
                        onClick={() => sortHandler(o.value)}
                        className="w-full text-left px-4 py-2 text-[12.5px] transition-colors hover:bg-white/5"
                        style={{ color: o.value === sort ? 'white' : 'rgba(255,255,255,0.6)' }}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-16">
        <div className="lg:grid lg:gap-8" style={{ gridTemplateColumns: '260px 1fr' }}>
          <aside className="hidden lg:block sticky top-40 self-start max-h-[calc(100vh-8.25rem)] overflow-y-auto overscroll-contain pr-2 pb-6 scrollbar-dark">
            <FilterSidebar
              categories={categories}
              brands={brands}
              tags={tags}
              activeCategoryId={activeCategoryId}
              setCategoryParams={setCategoryParams}
              activeBrandIds={activeBrandIds}
              toggleBrand={(id) => toggleListParam('brand', id)}
              activeTags={activeTags}
              toggleTag={(t) => toggleListParam('tag', t)}
              activeSpecial={activeSpecial}
              setSpecial={setSpecial}
              showLoyalty={showLoyaltyFilter}
              priceLo={lo}
              priceHi={hi}
              onPriceChange={onPriceChange}
              priceBins={priceBins}
              ratingMin={ratingMin}
              setRatingMin={setRatingMinParam}
            />
          </aside>

          <div>
            {isLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-10">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="aspect-4/5 w-full rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
                ))}
              </div>
            ) : displayedProducts.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-10">
                  {displayedProducts.map((p: any) => (
                    <ProductCard key={p.customId || p.id} product={p} vendor={vendor} />
                  ))}
                  {Array.from({ length: pendingCount }).map((_, i) => (
                    <div key={`pending-${i}`} ref={i === 0 ? loadMoreRef : undefined}>
                      <div className="aspect-4/5 w-full rounded-xl" style={{ background: 'rgba(255,255,255,0.04)' }} />
                      <div className="mt-3 flex flex-col gap-2">
                        <div className="h-4 w-3/4 rounded" style={{ background: 'rgba(255,255,255,0.04)' }} />
                        <div className="h-4 w-1/2 rounded" style={{ background: 'rgba(255,255,255,0.04)' }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div ref={pendingCount === 0 ? loadMoreRef : null} className="w-full pt-12 flex justify-center">
                  {isLoadingMore && (
                    <div className="flex items-center gap-2 text-[13px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                      <Loader2 className="h-4 w-4 animate-spin" /> Ачаалж байна...
                    </div>
                  )}
                  {!hasMore && (
                    <div className="text-[12px]" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      Бүх бүтээгдэхүүн ачаалагдлаа ({metadata.total})
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 py-24" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <PackageOpen className="h-8 w-8" />
                <div className="text-[14px]">Бүтээгдэхүүн олдсонгүй</div>
                {activeChips.length > 0 && (
                  <button onClick={clearAll} className="mt-2 inline-flex items-center gap-2 h-9 px-4 rounded-full text-[12.5px] font-medium" style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.14)' }}>
                    Шүүлтүүр арилгах
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Transition show={mobileFiltersOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setMobileFiltersOpen}>
          <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/60" />
          </TransitionChild>
          <div className="fixed inset-0 z-40 flex justify-end">
            <TransitionChild as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="translate-x-full">
              <DialogPanel className="relative flex w-[88%] max-w-sm flex-col overflow-hidden shadow-2xl rounded-l-2xl" style={{ background: '#0b0b0e' }}>
                <div className="h-14 px-5 flex items-center justify-between shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-center gap-2 text-white">
                    <Filter className="h-4 w-4" strokeWidth={2} />
                    <span className="text-[15px] font-semibold" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>Шүүлт</span>
                  </div>
                  <button onClick={() => setMobileFiltersOpen(false)} aria-label="Хаах" className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-colors hover:bg-white/5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto overscroll-contain px-5 scrollbar-dark">
                  <FilterSidebar
                    categories={categories}
                    brands={brands}
                    tags={tags}
                    activeCategoryId={activeCategoryId}
                    setCategoryParams={setCategoryParams}
                    activeBrandIds={activeBrandIds}
                    toggleBrand={(id) => toggleListParam('brand', id)}
                    activeTags={activeTags}
                    toggleTag={(t) => toggleListParam('tag', t)}
                    activeSpecial={activeSpecial}
                    setSpecial={setSpecial}
                    showLoyalty={showLoyaltyFilter}
                    priceLo={lo}
                    priceHi={hi}
                    onPriceChange={onPriceChange}
                    priceBins={priceBins}
                    ratingMin={ratingMin}
                    setRatingMin={setRatingMinParam}
                  />
                </div>
                <div className="p-4 shrink-0 flex gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  <button
                    onClick={clearAll}
                    className="flex-1 h-11 inline-flex items-center justify-center rounded-full text-[13px] font-medium transition-colors hover:bg-white/10"
                    style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
                  >
                    Цэвэрлэх
                  </button>
                  <button
                    onClick={() => setMobileFiltersOpen(false)}
                    className="flex-1 h-11 inline-flex items-center justify-center rounded-full text-[13px] font-semibold"
                    style={{ background: 'white', color: '#0a0a0c' }}
                  >
                    Үзэх
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
